import LetsDialSocketClient from '@letsdial/socket/src/LetsDialSocketClient'
import { ref, inject, onMounted, onUnmounted, Ref, watch, getCurrentInstance } from 'vue'
import { letsDialSocket } from './LetsDialSocketPlugin'

export default function useLetsDialSocket<ReportData = any>() {
  const instance = getCurrentInstance()
  const socket: LetsDialSocketClient | undefined = inject('socket') || letsDialSocket
  
  const listeners: Array<{
    event: string
    callback: (...args: any[]) => void
  }> = []
  
  const reportKey: Ref<string | undefined> = ref(undefined)
  const reportData: Ref<ReportData | undefined> = ref(undefined)
  
  if (!socket) {
    throw Error('Socket Plugin Not Loaded')
  }

  /**
   * Register a listener that will be automatically cleaned up on unmount.
   * IMPORTANT: Use named functions to ensure proper removal.
   */
  const listen = (event: string, callback: (...args: any[]) => void) => {
    listeners.push({ event, callback })
    // If called after mount, register immediately
    if (instance?.isMounted) {
      socket.on(event, callback)
    }
  }

  const setReportData = (data: ReportData) => {
    reportData.value = data
  }

  const getStream = (
    stream: string,
    params: any,
    callback: (data: any) => void,
  ) => {
    socket.emit(`join:${stream}`, params)
    socket.on(stream, callback)
    const leave = () => {
      socket.off(stream, callback)
      socket.emit(`leave:${stream}`, params)
    }
    // Only register unmount hook if we are in a component context
    if (instance) {
      onUnmounted(() => leave())
    }
    return { leave }
  }

  watch(reportKey, (key, oldKey) => {
    if (oldKey) {
      socket.off(`report:${oldKey}:data`, setReportData)
      socket.emit(`report:leave`, oldKey)
    }
    if (key) {
      socket.on(`report:${key}:data`, setReportData)
      socket.emit(`report:join`, key)
    }
  })

  onMounted(() => {
    listeners.forEach(({ event, callback }) => {
      socket.on(event, callback)
    })
    if (reportKey.value) {
      socket.on(`report:${reportKey.value}:data`, setReportData)
      socket.emit(`report:join`, reportKey.value)
    }
  })

  onUnmounted(() => {
    listeners.forEach(({ event, callback }) => {
      socket.off(event, callback)
    })
    if (reportKey.value) {
      socket.off(`report:${reportKey.value}:data`, setReportData)
      socket.emit(`report:leave`, reportKey.value)
    }
    // Clear local registry
    listeners.length = 0
  })

  return {
    socket,
    listen,
    reportKey,
    reportData,
    getStream,
  }
}
